import { useLocation } from 'react-router';
import { resendEmailLink } from '../modules/users';
import { Tooltip } from '@mui/material';

export const addDataToLocalStorage = (data) => {
  if (!data?.auth_token) return;

  localStorage.setItem('token', `Bearer ${data.auth_token}`);
  localStorage.setItem('user', JSON.stringify(data.user || {}));
  localStorage.setItem('role', JSON.stringify(data.user?.role || {}));
  localStorage.setItem('organization', JSON.stringify(data.organization || {}));
};

export const formatDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0'); // Add 1 because months are 0-indexed
  const day = String(d.getDate()).padStart(2, '0');
  return `${day}-${month}-${year}`;
};

export const convertToDateFirst = (date) => {
  const defaultOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  const dateString = new Date(date).toLocaleString(undefined, defaultOptions);
  return dateString;
};

export const toInputDateFormat = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};


export const getDateAndTime = (date, options) => {
  if (!date) return '';
  const dateObj = new Date(date);

  const defaultOptions = {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };

  const finalOptions = { ...defaultOptions, ...options };
  const visibleDate = dateObj.toLocaleString(undefined, finalOptions).replace(',', '');

  // Format for tooltip: dd-mm-yyyy
  const tooltipDate = `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;

  return (
    <Tooltip title={tooltipDate} arrow>
      <span>{visibleDate}</span>
    </Tooltip>
  );
};

export const getLatestIteration = (iterations) => {
  if (!iterations || iterations.length === 0) return null;
  return iterations.reduce((latest, iteration) =>
    new Date(iteration.created_at) > new Date(latest.created_at)
      ? iteration
      : latest
  );
};

export const formatElapsedTime = (elapsedTime) => {
  const totalSeconds = Math.floor(elapsedTime / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0'
  )}:${String(seconds).padStart(2, '0')}`;
};

export const openNewWindow = async (url) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) {
    newWindow.opener = null;
  } else {
    console.error('Failed to open new window');
  }
};

export const checkIssueIsNewOrExisting = (issueDate, executionDate) => {
  // Convert the date strings to UTC Date objects
  const issueDateObj = new Date(issueDate);
  const executionDateObj = new Date(executionDate);
  if (issueDateObj.getTime() >= executionDateObj.getTime()) {
    return 'New';
  } else {
    return 'Existing';
  }
};
export const formatPlaceholder = (body) => {
  return <span className="form-placeholder">{body || 'Not provided'}</span>;
};

export const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export const coloursArray = [
  '#a569bd', '#2471a3', '#d68910', '#2e86c1',
  '#a93226', '#28b463', '#7d3c98', '#d4ac0d',
  '#138d75', '#e74c3c', '#229954', '#ba4a00',
  '#f1948a', '#85c1e9', '#17a589', '#707b7c',
  '#a6acaf', '#e67e22', '#229954',
];

export function getRandomColor() {
  const randomNumber = Math.floor(Math.random() * coloursArray.length);
  return coloursArray[randomNumber];
}

export function generateUniqueColors(items, getRandomColor) {
  const usedColors = new Set();
  return items.map(() => {
    let color;
    do {
      color = getRandomColor();
    } while (usedColors.has(color));
    usedColors.add(color);
    return color;
  });
}

export function createQueryString(params) {
  return Object.keys(params)
    .filter((key) => params[key] !== 'All' && params[key]?.length !== 0)
    .flatMap((key) => {
      const value = params[key];
      if (Array.isArray(value)) {
        return value.map(
          (item) => `${encodeURIComponent(key)}[]=${encodeURIComponent(item)}`
        );
      } else {
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      }
    })
    .join('&');
}

export const formatDateRange = (dateFilters) => {
  const { startDate, endDate, defaultRange } = dateFilters;

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString();
  };

  if (!defaultRange && startDate && endDate) {
    return `(Showing results from ${formatDate(startDate)} to ${formatDate(endDate)})`;
  }
  return '(Showing results for last 4 weeks)';
};

export const getDefaultDateRange = () => {
  const today = new Date();
  const fourWeeksAgo = new Date(today);
  fourWeeksAgo.setDate(today.getDate() - 28);

  return {
    startDate: fourWeeksAgo.toISOString().split('T')[0],
    endDate: today.toISOString().split('T')[0],
  };
};


export const formatCommentWithDisplayNames = (comment, users) => {
  return comment.replace(/@\[(.+?)\]/g, (match, userId) => {
    const user = users.find((user) => user?.id === userId);
    return user ? `@${user.full_name}` : '';
  });
};

export const formatCommentWithAccountIds = (comment, users) => {
  const userMap = new Map(
    users.map((user) => [user?.full_name.toLowerCase(), user?.id])
  );

  const replaceDisplayNames = (text) => {
    let updatedText = text;

    userMap.forEach((accountId, full_name) => {
      const regex = new RegExp(`@\\b${escapeRegExp(full_name)}\\b`, 'gi');
      updatedText = updatedText.replace(regex, `@[${accountId}]`);
    });

    return updatedText;
  };

  const escapeRegExp = (string) => {
    return string?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  return replaceDisplayNames(comment);
};

export const maskLongToken = (token) => {
  const visibleStart = 50;
  const visibleEnd = 10;
  const totalLength = token.length;

  if (totalLength <= visibleStart + visibleEnd) {
    return token;
  }

  const startPart = token.substr(0, visibleStart);
  const endPart = token.substr(totalLength - visibleEnd);
  const maskedPart = '*'.repeat(totalLength - (visibleStart + visibleEnd));

  return `${startPart}${maskedPart}${endPart}`;
};

export const getExpiry = (createdAt, expiryTime) => {
  const expiryMilliSeconds = new Date(createdAt).getTime() + (expiryTime * 1000);
  const currentMilliSeconds = Date.now();
  return currentMilliSeconds > expiryMilliSeconds;
};

export function stableSort(array, comparator) {
  const stabilizedThis = array?.map((el, index) => [el, index]);

  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1]; // Maintain original order if equal
  });

  return stabilizedThis.map((el) => el[0]);
}

export function descendingComparator(a, b, orderBy) {
  let aValue = a?.[orderBy] ?? "";
  let bValue = b?.[orderBy] ?? "";

  if (typeof aValue === "object" && aValue !== null) aValue = aValue.name ?? "";
  if (typeof bValue === "object" && bValue !== null) bValue = bValue.name ?? "";
  const dateFields = ["created_at", "updated_at", "lastUpdated"];

  // Compare dates
  if (dateFields.includes(orderBy)) {
    aValue = new Date(aValue);
    bValue = new Date(bValue);
    return bValue - aValue;
  } else if (typeof aValue === "string" && typeof bValue === "string") {
    // Handle sorting of IDs like "C1-TC10"
    return compareAlphanumeric(aValue, bValue);
  } else {
    // Compare other types
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
  }
  return 0;
}

// Function to properly compare alphanumeric strings
function compareAlphanumeric(a, b) {
  const regex = /(\D+)|(\d+)/g; // Splits into non-numeric and numeric parts

  const aParts = a.match(regex) || [];
  const bParts = b.match(regex) || [];

  for (let i = 0; i < Math.min(aParts.length, bParts.length); i++) {
    const aPart = aParts[i];
    const bPart = bParts[i];

    if (aPart !== bPart) {
      // Compare numbers as numbers, and strings as strings
      const aNum = Number(aPart);
      const bNum = Number(bPart);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      return aPart.localeCompare(bPart, undefined, { numeric: true });
    }
  }

  return aParts.length - bParts.length;
}

export function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export const resendLinkToUser = async (email, type) => {
  if (!email) {
    throw new Error('Please enter your email address to resend the confirmation email.');
  }
  const payload = {
    email,
    type,
  };
  try {
    await resendEmailLink(payload);
    return 'An email has been sent to your inbox. Please check your email';
  } catch (error) {
    throw new Error(error.message || 'Failed to resend email. Please try again.');
  }
};

export const renderCommentBody = (body, users) => {
  const mentionRegex = /@\[(.+?)\]/g;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const segments = [];
  let lastIndex = 0;
  let match;

  const userMap = new Map(users.map((user) => [user?.id, user?.full_name]));

  const processUrlSegments = (text) => {
    const urlSegments = text.split(urlRegex);
    return urlSegments.map((segment, index) => {
      if (urlRegex.test(segment)) {
        return (
          <a key={`url-${index}`} href={segment} target="_blank" rel="noopener noreferrer" style={{ color: 'blue' }}>
            {segment}
          </a>
        );
      } else {
        return segment;
      }
    });
  };

  while ((match = mentionRegex.exec(body)) !== null) {
    const [_, id] = match;
    const displayName = userMap.get(id);
    if (match.index > lastIndex) {
      const textSegment = body.slice(lastIndex, match.index);
      segments.push(...processUrlSegments(textSegment));
    }

    if (displayName) {
      segments.push(
        <span key={`mention-${match.index}`} style={{ color: 'blue' }}>
          @{displayName}
        </span>
      );
    }

    lastIndex = mentionRegex.lastIndex;
  }

  if (lastIndex < body.length) {
    const remainingText = body.slice(lastIndex);
    segments.push(...processUrlSegments(remainingText));
  }

  return <>{segments}</>;
};

export const renderLinks = (body) => {
  const urlRegex = /((https?:\/\/|www\.)[^\s]+)/g;
  const segments = [];
  let lastIndex = 0;
  let match;

  while ((match = urlRegex.exec(body)) !== null) {
    if (match.index > lastIndex) {
      const textSegment = body.slice(lastIndex, match.index);

      segments.push(textSegment);
    }

    let url = match[0];
    if (url.startsWith('www.')) {
      url = 'http://' + url;
    }

    segments.push(
      <a
        key={match.index}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'blue' }}
      >
        {match[0]}
      </a>
    );

    lastIndex = urlRegex.lastIndex;
  }

  if (lastIndex < body.length) {
    const remainingText = body.slice(lastIndex);
    segments.push(remainingText);
  }

  return <>{segments}</>;
};

export const checkTechLayerCompatibility = (selectedSuites, techLayer, allSuites) => {
  if (!techLayer) {
    return null;
  }

  const compatibilityMatrix = {
    'API/Integration': ['API'],
    'Web Front-end': ['Web'],
    'Mobile Front-end': ['Mobile Responsive', 'Native Mobile App', 'Hybrid Mobile App'],
    'Database': ['Database Testing'],
    'Contract': ['API'],
  };

  const compatiblePlatforms = compatibilityMatrix[techLayer] || [];

  const incompatibleSuites = selectedSuites
    .map(suiteId => allSuites?.find(suite => suite?.id === suiteId))
    .filter(suite => suite && suite?.platforms?.length > 0 && !suite?.platforms.some(platform => compatiblePlatforms.includes(platform)))
    .map(suite => suite?.name);

  if (incompatibleSuites.length > 0) {
    return `The selected test suite(s) (${incompatibleSuites.join(', ')}) 
    are incompatible with the chosen technology layer. Consider adding the relevant 
    platforms to the test suite(s) for better compatibility.`;
  }

  return null;
};

export const checkCountryCompatibility = (
  testCaseCountries,
  testSuiteIds,
  allTestSuites
) => {
  if (!testCaseCountries || testCaseCountries.length === 0) {
    return null;
  }

  const warnings = [];

  for (const suiteId of testSuiteIds) {
    const testSuite = allTestSuites.find((suite) => suite.id === suiteId);

    if (!testSuite) continue;

    const testSuiteGeographies = new Set(testSuite.geographies || []);
    const incompatibleCountries = [];

    for (const country of testCaseCountries) {
      if (!testSuiteGeographies.has(country)) {
        incompatibleCountries.push(country);
      }
    }

    if (incompatibleCountries.length > 0) {
      warnings.push({
        testSuiteId: suiteId,
        testSuiteName: testSuite.name,
        countries: incompatibleCountries,
      });
    }
  }

  return warnings.length > 0 ? warnings : null;
};


export const getRoleLabel = (role) => {
  const roleMapping = {
    admin: 'Project Admin',
    member: 'Team Member',
    'org-admin': 'Organization Admin',
    'org-manager': 'Organization Manager',
    'team-member': 'Team Member',
    'executive-manager': 'Executive Manager',
    'super-admin': 'Super Admin',
  };

  return roleMapping[role] || 'Other';
};


export const truncateText = (text, maxLength = 15) => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

export const getPriorityColor = (priority) => {
  const priorityColors = {
    Highest: '#a93226',
    High: '#e74c3c',
    Medium: '#d68910',
    Low: '#2e86c1',
    Lowest: '#2e4053',
    Critical: '#ba4a00',
  };

  return priorityColors[priority] || getRandomColor();
}

const FormattedJsonDisplay = ({ jsonString }) => {
  if (!jsonString) {
    return null;
  }

  try {
    const parsedObject = JSON.parse(jsonString);
    const formattedJson = JSON.stringify(parsedObject, null, 2);

    return (
      <pre
        style={{
          whiteSpace: 'pre-wrap',
          fontSize: '0.7em',
          margin: 0,
          maxHeight: '120px',
          overflowY: 'auto'
        }}
      >
        {formattedJson}
      </pre>
    );
  } catch (e) {
    const truncatedString = jsonString.length > 50 ? `${jsonString.substring(0, 50)}...` : jsonString;

    return (
      <span title={jsonString}>
        {truncatedString}
      </span>
    );
  }
};

export default FormattedJsonDisplay;


export const formatPhoneNumber = (phone) => {
  if (!phone) return '';

  const str = phone.toString();
  if (str.length > 10) {
    const countryCode = str.slice(0, str.length - 10);
    const number = str.slice(-10);
    return `+${countryCode} ${number}`;
  }

  return str;
};